import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { appsAPI } from '../api/appsAPI';
import UserProfileDropdown from '../../../components/UserProfileDropdown/UserProfileDropdown';
import { Logo_black } from '../../../components/Logo/Logo';
import { API_CONFIG } from '../../../config/api';
import './WindmillGame.css';

const getGameApiUrl = (path) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_CONFIG.baseURL}${normalizedPath}`;
};

const VIDEO_BASE = 'https://pathbuddy-media.lon1.cdn.digitaloceanspaces.com/Games/Apple-The%20Legacy%20Test/';
const VIDEO_MAP = {
  'scene1_intro.mp4': `${VIDEO_BASE}intro.mp4`,
  'scene2_stage.mp4': `${VIDEO_BASE}walking%20to%20stage.mp4`,
  'scene3_lab.mp4': `${VIDEO_BASE}operations.mp4`,
  'scene5_emerging_store.mp4': `${VIDEO_BASE}quite-applestore.mp4`,
  'scene6_premium_store.mp4': `${VIDEO_BASE}buys_apple_store.mp4`,
  'scene7_apple_car_street.mp4': `${VIDEO_BASE}applecar.mp4`,
  'ending_finale.mp4': `${VIDEO_BASE}board-finale.mp4`,
};

const sceneByVideo = {
  'scene1_intro.mp4': 'capital_structure',
  'scene2_stage.mp4': 'eps_signal',
  'scene3_lab.mp4': 'apple_car_npv',
  'scene5_emerging_store.mp4': 'market_sizing',
  'scene6_premium_store.mp4': 'premium_vs_volume',
  'scene7_apple_car_street.mp4': 'future_valuation',
};

const AppleGame = ({ appId: appIdProp, onComplete, parentAppId }) => {
  const { id: idFromParams } = useParams();
  const navigate = useNavigate();
  const id = appIdProp != null ? appIdProp : idFromParams;
  const { user } = useAuth();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuestion, setShowQuestion] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [currentVideo, setCurrentVideo] = useState('scene1_intro.mp4');
  const [pendingVideo, setPendingVideo] = useState(null);
  const [progressPct, setProgressPct] = useState(0);
  const [visitedScenes, setVisitedScenes] = useState(new Set(['scene1_intro.mp4']));

  const videoRef = useRef(null);
  const userName = user?.name || 'Student';
  const userEmail = user?.email || 'student@pathbuddy.com';

  useEffect(() => {
    const fetchApp = async () => {
      try {
        setLoading(true);
        const appData = await appsAPI.getAppById(id);
        setApp(appData);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchApp();
  }, [id]);

  useEffect(() => {
    setProgressPct(Math.min((visitedScenes.size / 7) * 100, 100));
  }, [visitedScenes]);

  const getVideoUrl = (filename) => VIDEO_MAP[filename] || `/videos/${filename}`;

  const playVideo = (filename) => {
    setCurrentVideo(filename);
    if (sceneByVideo[filename]) {
      setVisitedScenes((prev) => new Set(prev).add(filename));
    }
    const el = videoRef.current;
    if (!el) return;
    el.src = getVideoUrl(filename);
    el.load();
    el.play().catch(() => {});
  };

  const fetchQuestion = async (questionId) => {
    const response = await fetch(getGameApiUrl(`/api/apple-game/question/${questionId}`));
    const question = await response.json();
    setCurrentQuestion(question);
    setShowQuestion(true);
  };

  const onVideoEnded = () => {
    const questionId = sceneByVideo[currentVideo];
    if (!questionId) {
      setShowQuestion(true);
      setCurrentQuestion({
        title: 'CASE STUDY COMPLETE',
        subtitle: 'The Legacy Test Finished',
        question: 'You have completed the Apple strategic finance game.',
        isComplete: true,
      });
      return;
    }
    fetchQuestion(questionId).catch(() => {});
  };

  const selectOption = async (choice) => {
    const questionId = sceneByVideo[currentVideo];
    if (!questionId) return;
    const response = await fetch(getGameApiUrl('/api/apple-game/answer'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question_id: questionId, choice, user_id: user?.id || 'default' }),
    });
    const result = await response.json();
    setFeedback(result);
    setPendingVideo(result.next_video || 'ending_finale.mp4');
    setShowQuestion(false);
    setShowFeedback(true);
  };

  const continueCaseStudy = () => {
    setShowFeedback(false);
    if (!pendingVideo) {
      onComplete?.();
      return;
    }
    playVideo(pendingVideo);
    setPendingVideo(null);
  };

  if (loading) {
    return <div className="windmill-game-loading"><p>Loading game...</p></div>;
  }

  return (
    <div className="windmill-game-page">
      <header className="windmill-game-header">
        <div className="windmill-game-header-content">
          <div className="windmill-game-left-section">
            <button className="back-button" onClick={() => navigate(parentAppId ? `/apps-store/${parentAppId}/experience` : `/apps-store/${id}`)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" /></svg>
            </button>
            <div className="windmill-game-logo"><Logo_black onClick={() => navigate('/apps-store')} /></div>
            <h1 className="windmill-game-title">{app?.title || 'Apple Game'}</h1>
          </div>
        </div>
      </header>

      <main className="windmill-game-main">
        <div className="windmill-video-container">
          <video ref={videoRef} className="windmill-main-video" autoPlay muted playsInline onEnded={onVideoEnded} onLoadStart={() => setVideoLoading(true)} onCanPlay={() => setVideoLoading(false)}>
            <source src={getVideoUrl('scene1_intro.mp4')} type="video/mp4" />
          </video>

          {showQuestion && currentQuestion && (
            <div className="windmill-overlay active">
              <div className="windmill-question-panel">
                {currentQuestion.isComplete ? (
                  <>
                    <div className="windmill-question-title">{currentQuestion.title}</div>
                    <div className="windmill-question-subtitle">{currentQuestion.subtitle}</div>
                    <div className="windmill-question-text">{currentQuestion.question}</div>
                    <button className="windmill-continue-btn" onClick={() => (onComplete ? onComplete() : navigate(parentAppId ? `/apps-store/${parentAppId}/experience` : `/apps-store/${id}`))}>Continue</button>
                  </>
                ) : (
                  <>
                    <div className="windmill-question-title">{currentQuestion.title}</div>
                    <div className="windmill-question-subtitle">{currentQuestion.subtitle}</div>
                    <div className="windmill-question-text">{currentQuestion.question}</div>
                    <div className="windmill-options">
                      {Object.keys(currentQuestion.options || {}).map((key) => (
                        <div key={key} className="windmill-option-button" onClick={() => selectOption(key)}>
                          <span className="windmill-option-letter">{key}</span>
                          <div className="windmill-option-title">{currentQuestion.options[key].text}</div>
                          <div className="windmill-option-desc">{currentQuestion.options[key].desc}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {showFeedback && feedback && (
            <div className={`windmill-feedback ${feedback.correct ? 'correct' : 'incorrect'}`}>
              <div className="windmill-feedback-title">{feedback.correct ? 'EXCELLENT ANALYSIS' : 'THOUGHTFUL APPROACH'}</div>
              <div className="windmill-feedback-text">{feedback.explanation}</div>
              {feedback.calculation && <div className="windmill-feedback-text"><strong>Calculation:</strong><br />{feedback.calculation}</div>}
              <button className="windmill-continue-btn" onClick={continueCaseStudy}>Continue</button>
            </div>
          )}

          {videoLoading && <div className="windmill-loading">Loading...</div>}
          <div className="windmill-progress-bar"><div className="windmill-progress-fill" style={{ width: `${progressPct}%` }}></div></div>
        </div>
      </main>
    </div>
  );
};

export default AppleGame;
